import type { PortalLoginStatus, PortalActionTokenState } from './types';

export function getAuthUnavailableMessage(flow: 'login' | 'reset' | 'activation') {
  switch (flow) {
    case 'login':
      return 'Aanmelden lukt nu even niet doordat de beveiligde aanmeldservice niet bereikbaar is. Probeer het zo opnieuw.';
    case 'reset':
      return 'Wachtwoordherstel lukt nu even niet doordat de beveiligde resetservice niet bereikbaar is. Probeer het later opnieuw.';
    case 'activation':
      return 'Accountactivatie lukt nu even niet doordat de beveiligde activatieservice niet bereikbaar is. Probeer het later opnieuw.';
    default:
      return null;
  }
}

export function getAuthRateLimitedMessage(flow: 'reset' | 'activation') {
  switch (flow) {
    case 'reset':
      return 'Je hebt te vaak geprobeerd je wachtwoord opnieuw in te stellen. Wacht even en probeer het daarna opnieuw.';
    case 'activation':
      return 'Je hebt te vaak geprobeerd deze activatie af te ronden. Wacht even en probeer het daarna opnieuw.';
    default:
      return null;
  }
}

export function getAuthErrorMessage(error?: string | null, code?: string | null) {
  switch (error) {
    case 'access_denied':
      return 'Je toegang is niet meer geldig. Log opnieuw in of neem contact op met je beheerder.';
    case 'auth_unavailable':
      return getAuthUnavailableMessage('login');
    case 'CredentialsSignin':
      if (code === 'rate_limited') {
        return 'Er zijn te veel inlogpogingen gedaan. Wacht 15 minuten zonder opnieuw te proberen en probeer het daarna nog eens.';
      }
      if (code === 'auth_unavailable') {
        return getAuthUnavailableMessage('login');
      }

      return 'De combinatie van e-mailadres en wachtwoord is ongeldig.';
    case 'Configuration':
      return 'Aanmelden is in deze omgeving nog niet beschikbaar.';
    default:
      return null;
  }
}

export function getAuthStatusMessage(status?: string | null) {
  switch (status as PortalLoginStatus | undefined) {
    case 'activated':
      return 'Je account is geactiveerd. Log in met je nieuwe wachtwoord.';
    case 'reset':
      return 'Je wachtwoord is bijgewerkt. Log in met je nieuwe wachtwoord.';
    default:
      return null;
  }
}

export function getTokenStateMessage(flow: 'activation' | 'reset', state: PortalActionTokenState) {
  const isActivationFlow = flow === 'activation';

  switch (state) {
    case 'READY':
      return null;
    case 'INVALID':
      return {
        title: isActivationFlow ? 'Deze activatielink is ongeldig' : 'Deze resetlink is ongeldig',
        description: isActivationFlow
          ? 'Controleer of je de meest recente uitnodigingslink gebruikt.'
          : 'Controleer of je de meest recente resetlink gebruikt.',
      };
    case 'EXPIRED':
      return {
        title: isActivationFlow ? 'Deze activatielink is verlopen' : 'Deze resetlink is verlopen',
        description: isActivationFlow
          ? 'Vraag een nieuwe uitnodiging aan bij VlamSafe of je beheerder.'
          : 'Vraag opnieuw een wachtwoordreset aan om verder te gaan.',
      };
    case 'CONSUMED':
      return {
        title: isActivationFlow ? 'Deze activatielink is al gebruikt' : 'Deze resetlink is al gebruikt',
        description: isActivationFlow
          ? 'Je account lijkt al geactiveerd te zijn. Log in met je emailadres en wachtwoord.'
          : 'Vraag een nieuwe resetlink aan als je opnieuw een wachtwoord wilt instellen.',
      };
    case 'BLOCKED':
      return {
        title: isActivationFlow ? 'Deze activatie is geblokkeerd' : 'Deze resetactie is geblokkeerd',
        description:
          'Je portaaltoegang kan momenteel niet via deze link worden afgerond. Neem contact op met VlamSafe.',
      };
    default:
      return null;
  }
}
